// Phase 5Z.38 — Bulk Zeiterfassung Import Modal
// Single driver, many days. Asks: who, default vehicle, overwrite toggle.
import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Download, X, FileSpreadsheet, AlertCircle, CheckCircle2, Info } from "lucide-react";
import EmployeePicker from "@/components/EmployeePicker";
import VehiclePicker from "@/components/VehiclePicker";

export default function ZeiterfassungBulkImportModal({ open, onClose, onDone }) {
  const [employeeId, setEmployeeId] = useState("");
  const [defaultVehicleId, setDefaultVehicleId] = useState("");
  const [overwrite, setOverwrite] = useState(false);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);

  if (!open) return null;

  function reset() {
    setEmployeeId("");
    setDefaultVehicleId("");
    setOverwrite(false);
    setReport(null);
  }
  function close() {
    if (busy) return;
    reset();
    onClose?.();
  }

  async function downloadTemplate() {
    try {
      const params = employeeId ? { employee_id: employeeId } : {};
      const res = await api.get("/imports/time-entries/template", {
        responseType: "blob",
        params,
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Zeiterfassung_Vorlage${employeeId ? "_" + employeeId.slice(0, 8) : ""}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Vorlage heruntergeladen");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Vorlage konnte nicht erstellt werden");
    }
  }

  async function pickFile() {
    if (!employeeId) {
      toast.error("Bitte zuerst einen Fahrer wählen");
      return;
    }
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".xlsx";
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (!file.name.toLowerCase().endsWith(".xlsx")) {
        toast.error("Bitte eine .xlsx-Datei wählen");
        return;
      }
      setBusy(true);
      const fd = new FormData();
      fd.append("file", file);
      fd.append("employee_id", employeeId);
      fd.append("default_vehicle_id", defaultVehicleId || "");
      fd.append("overwrite", overwrite ? "true" : "false");
      try {
        const { data } = await api.post("/imports/time-entries", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 90_000,
        });
        setReport({ ...data, fileName: file.name });
        if (onDone) onDone();
        const msg = `${data.created || 0} neu · ${data.updated || 0} aktualisiert · ${data.skipped || 0} übersprungen`;
        if ((data.errors || []).length === 0) toast.success(`Import OK — ${msg}`);
        else toast.warning(`Import fertig — ${msg} · ${data.errors.length} Hinweise`);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Import fehlgeschlagen");
      } finally {
        setBusy(false);
      }
    };
    input.click();
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-[60]" data-testid="zeiterfassung-bulk-modal">
      <div className="bg-zinc-900 border border-zinc-800 rounded-md w-full max-w-2xl max-h-[88vh] overflow-y-auto">
        <div className="p-5 border-b border-zinc-800 flex items-center justify-between">
          <div>
            <h3 className="font-display font-semibold text-zinc-50 inline-flex items-center gap-2">
              <FileSpreadsheet className="w-4 h-4 text-emerald-400" />
              Zeiterfassung — Bulk-Import (Monats-Vorlage)
            </h3>
            <p className="text-xs text-zinc-500 mt-0.5">
              Eine Excel-Datei für einen Fahrer mit allen Tagen des Monats hochladen.
            </p>
          </div>
          <button onClick={close} disabled={busy} className="text-zinc-400 hover:text-zinc-100 disabled:opacity-50" data-testid="zeit-bulk-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {!report && (
          <div className="p-5 space-y-5">
            {/* Step 1 — Employee */}
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-1.5">
                1. Für wen importieren? *
              </label>
              <EmployeePicker
                value={employeeId}
                onChange={setEmployeeId}
                testid="zeit-bulk-emp-picker"
                placeholder="— Fahrer wählen —"
                filter={(e) => e.role === "fahrer"}
              />
            </div>

            {/* Step 2 — Default vehicle */}
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-1.5">
                2. Standard-Fahrzeug (optional)
              </label>
              <VehiclePicker
                value={defaultVehicleId}
                onChange={setDefaultVehicleId}
                testid="zeit-bulk-veh-picker"
                placeholder="— ohne Standard —"
              />
              <p className="text-[10px] text-zinc-500 mt-1.5 leading-relaxed">
                Wird verwendet, wenn in der Excel-Spalte „Fahrzeug-Kennzeichen" nichts eingetragen ist.
                Pro Zeile kann das Standard-Fahrzeug per Kennzeichen überschrieben werden.
              </p>
            </div>

            {/* Step 3 — Overwrite toggle */}
            <div className="bg-zinc-950 border border-zinc-800 rounded p-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={overwrite}
                  onChange={(e) => setOverwrite(e.target.checked)}
                  data-testid="zeit-bulk-overwrite"
                  className="w-4 h-4 mt-0.5 rounded accent-amber-600"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium text-zinc-200">Vorhandene Einträge überschreiben</div>
                  <div className="text-[11px] text-zinc-500 mt-0.5 leading-relaxed">
                    Aus: vorhandene Tage werden <strong>übersprungen</strong> (sicher, niemand verliert Daten).<br/>
                    An: vorhandene Tage werden <strong>komplett ersetzt</strong> (gut für Korrekturen).
                  </div>
                </div>
              </label>
            </div>

            {/* Step 4 — Template + Upload */}
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-1.5">
                3. Vorlage herunterladen → ausfüllen → hochladen
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={downloadTemplate}
                  data-testid="zeit-bulk-template-dl"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-amber-600/15 border border-amber-500/40 text-amber-300 hover:bg-amber-600/25 text-sm font-medium transition"
                >
                  <Download className="w-4 h-4" /> Vorlage herunterladen
                </button>
                <button
                  type="button"
                  onClick={pickFile}
                  disabled={!employeeId || busy}
                  data-testid="zeit-bulk-upload-btn"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-emerald-600/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-600/25 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  <Upload className="w-4 h-4" /> {busy ? "Lade…" : "Excel hochladen"}
                </button>
              </div>
            </div>

            {/* Hints */}
            <div className="rounded-md border border-blue-500/30 bg-blue-500/5 p-3 text-[12px] text-blue-200 leading-relaxed flex items-start gap-2">
              <Info className="w-4 h-4 mt-0.5 shrink-0 text-blue-400" />
              <div>
                <strong>Hinweise zur Vorlage:</strong>
                <ul className="list-disc list-inside mt-1 space-y-0.5">
                  <li><code className="font-mono text-[11px]">Datum</code>, <code className="font-mono text-[11px]">Arbeitsbeginn</code> &amp; <code className="font-mono text-[11px]">Arbeitsende</code> sind Pflichtfelder.</li>
                  <li>Zeiten als <code className="font-mono text-[11px]">HH:MM</code> (z. B. 06:00, 14:30).</li>
                  <li>Datum als <code className="font-mono text-[11px]">TT.MM.JJJJ</code> oder <code className="font-mono text-[11px]">JJJJ-MM-TT</code>.</li>
                  <li>Co-Pilot: <code className="font-mono text-[11px]">ja</code>/<code className="font-mono text-[11px]">nein</code>.</li>
                  <li>Pause-Minuten als ganze Zahl (z. B. 30, 45).</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {report && (
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-4 gap-3">
              <Stat label="Neu" value={report.created || 0} tone="emerald" icon={CheckCircle2} testid="zeit-bulk-stat-created" />
              <Stat label="Aktualisiert" value={report.updated || 0} tone="blue" icon={CheckCircle2} testid="zeit-bulk-stat-updated" />
              <Stat label="Übersprungen" value={report.skipped || 0} tone="amber" icon={AlertCircle} testid="zeit-bulk-stat-skipped" />
              <Stat label="Hinweise" value={(report.errors || []).length} tone={(report.errors || []).length > 0 ? "rose" : "zinc"} icon={AlertCircle} testid="zeit-bulk-stat-errors" />
            </div>
            {(report.errors || []).length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-2">Hinweise pro Zeile</div>
                <div className="bg-zinc-950 border border-zinc-800 rounded max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-zinc-950 border-b border-zinc-800">
                      <tr className="text-zinc-500">
                        <th className="text-left px-2 py-1.5 font-medium">Zeile</th>
                        <th className="text-left px-2 py-1.5 font-medium">Feld</th>
                        <th className="text-left px-2 py-1.5 font-medium">Hinweis</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(report.errors || []).map((e, i) => (
                        <tr key={i} className="border-b border-zinc-800/40 last:border-b-0">
                          <td className="px-2 py-1.5 font-mono text-zinc-300">{e.row}</td>
                          <td className="px-2 py-1.5 text-amber-300">{e.field}</td>
                          <td className="px-2 py-1.5 text-zinc-300">{e.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="p-4 border-t border-zinc-800 flex justify-end gap-2">
          {report && (
            <button onClick={() => setReport(null)} data-testid="zeit-bulk-import-more"
              className="text-sm px-3 py-2 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-200 font-medium">
              Weitere importieren
            </button>
          )}
          <button onClick={close} disabled={busy} data-testid="zeit-bulk-done"
            className="text-sm px-4 py-2 rounded bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium">
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, tone, icon: Icon, testid }) {
  const map = {
    emerald: "bg-emerald-500/10 border-emerald-500/40 text-emerald-300",
    blue: "bg-blue-500/10 border-blue-500/40 text-blue-300",
    amber: "bg-amber-500/10 border-amber-500/40 text-amber-300",
    rose: "bg-rose-500/10 border-rose-500/40 text-rose-300",
    zinc: "bg-zinc-800/40 border-zinc-700 text-zinc-300",
  };
  return (
    <div className={`rounded-md border p-3 ${map[tone]}`} data-testid={testid}>
      <div className="flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5" />
        <span className="text-[10px] uppercase tracking-wider font-bold">{label}</span>
      </div>
      <div className="mt-1 text-2xl font-extrabold tabular-nums">{value}</div>
    </div>
  );
}
